import { useTheme } from '@mui/material/styles'
import React from 'react'
import {
  Legend,
  Line,
  LineChart as RechartLineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type YAxisProps
} from 'recharts'
import zIndexes from '../../theme/zIndex'
import { type CustomTooltipProps, type LineChartProps, type LineConfig } from './types'

const LineChart = ({
  containerWidth = '100%',
  containerHeight = '100%',
  data,
  xAxis,
  yAxis,
  getConfigByDataKey,
  renderTooltip,
  showLegend = false,
  lineOpacity,
  dataKeys,
  ...rest
}: LineChartProps): React.ReactElement => {
  const theme = useTheme()

  const defaultLineProps: Pick<LineConfig, 'type' | 'lineWidth' | 'lineColor' | 'lineOpacity'> = {
    type: 'monotone',
    lineWidth: 4,
    lineColor: theme.tokens.background.brand.main.default,
    lineOpacity: 1
  }

  const defaultXAxisProps = {
    tickLine: false,
    axisLine: { stroke: theme.tokens.border.main.tonal },
    tick: { fontWeight: theme.typography.fontWeightBold },
    stroke: theme.tokens.foreground.main.primary
  }

  const defaultYAxisProps: YAxisProps = {
    orientation: 'right',
    axisLine: false,
    tickLine: false,
    tick: { textAnchor: 'end' },
    stroke: theme.tokens.foreground.main.primary
  }

  const defaultYLabelProps: YAxisProps['label'] = {
    position: 'top',
    offset: 25,
    style: {
      fontFamily: theme.typography.h4.fontFamily,
      fontWeight: theme.typography.h4.fontWeight,
      fontSize: theme.typography.h4.fontSize,
      lineHeight: theme.typography.h4.lineHeight,
      letterSpacing: theme.typography.h4.letterSpacing,
      textAnchor: 'end',
      fill: theme.tokens.foreground.main.primary
    },
    dx: 10
  }

  const uniqueDataKeys = Array.from(new Set(dataKeys ?? data.flatMap((entry) => Object.keys(entry))))

  const yDataKeys = uniqueDataKeys.filter((key) => key !== 'x')

  const CustomTooltip = ({ active = false, payload }: CustomTooltipProps): React.ReactElement | null => {
    if (active && payload != null && payload?.length > 0 && renderTooltip != null) {
      return renderTooltip({ dataKey: payload[0].dataKey, payload: payload[0].payload })
    }

    return null
  }

  const yAxes = Array.isArray(yAxis) ? yAxis : [yAxis]

  return (
    <ResponsiveContainer width={containerWidth} height={containerHeight}>
      <RechartLineChart data={data} {...rest}>
        <XAxis
          dataKey="x"
          minTickGap={24}
          padding={{ right: 30 }}
          {...{
            ...defaultXAxisProps,
            ...xAxis
          }}
        />
        {yDataKeys.map((dataKey) => {
          const lineWithDefaults = { ...defaultLineProps, ...getConfigByDataKey?.(dataKey) }
          const { lineColor, lineWidth, lineOpacity, ...lineProps } = lineWithDefaults
          return (
            <Line
              key={dataKey}
              dataKey={dataKey}
              stroke={lineColor}
              strokeWidth={lineWidth}
              opacity={lineOpacity}
              {...lineProps}
              dot={false}
            />
          )
        })}
        {yAxes.map((yAxis) => (
          <YAxis
            key={yAxis?.yAxisId}
            {...{
              ...defaultYAxisProps,
              ...yAxis,
              label: {
                ...defaultYLabelProps,
                ...yAxis?.label
              }
            }}
          />
        ))}
        {renderTooltip != null && (
          <Tooltip
            content={<CustomTooltip />}
            wrapperStyle={{
              zIndex: zIndexes.chartTooltip,
              pointerEvents: 'none'
            }}
            allowEscapeViewBox={{ x: true, y: true }}
          />
        )}
        {showLegend ? <Legend /> : null}
      </RechartLineChart>
    </ResponsiveContainer>
  )
}
export default LineChart

export { LineChart }
export type { LineData } from './types'
